import { apiFetch } from './client'
import type { PricingStrategy } from './pricing'
import type { Kennung, ShippingPolicy } from './priceRollout'

/** Strategie je Angebot (Plan 2026-10-19): nur die Strategie-Auswahl der Child-Artikel eines eBay-Angebots. */

export interface OfferTreffer {
  item_id: string
  title: string | null
  url: string
  account_key: string
  varianten: number
}

export interface OfferVariante {
  stock_item_id: number
  sku: string
  name: string
  variation_key: string | null
  grundartikel: { sku: string; name: string } | null
  /** S3: Stern groß, DR klein, Einzelstück/×n/Set — wie in allen Rollout-Fenstern. */
  kennung?: Kennung | null
  /** S3: Versand des eBay-Angebots (inkl./zzgl.). */
  versand?: ShippingPolicy
  strategie: { amazon: PricingStrategy; base: PricingStrategy; uneinheitlich: boolean; hatte_zeile: boolean }
  auch_in: { item_id: string; title: string | null }[]
}

export interface EbayOffer {
  portal: 'ebay'
  item_id: string
  title: string | null
  url: string
  account_key: string
  varianten: OfferVariante[]
  preview_token: string
  stand: string
}

export interface BatchZeile {
  stock_item_id: number
  sku: string
  vorher: { amazon: PricingStrategy; base: PricingStrategy }
  nachher: { amazon: PricingStrategy; base: PricingStrategy }
  geaendert: boolean
}

export interface BatchResult {
  batch_id: string
  request_id: string
  item_id: string
  strategy: PricingStrategy
  geaendert: number
  unveraendert: number
  zeilen: BatchZeile[]
  wiederholt: boolean
}

export interface UndoResult { batch_id: string; wiederhergestellt: number[]; uebersprungen: number[] }

export interface BatchListEintrag {
  batch_id: string
  created_at: string
  actor_name: string | null
  item_id: string
  title: string | null
  strategy: PricingStrategy
  geaendert: number
  unveraendert: number
  rueckgaengig: { am: string; wiederhergestellt: number; uebersprungen: number } | null
}

export const OFFER_BATCH_MAX = 300

export async function searchOffers(q: string): Promise<OfferTreffer[]> {
  const res = await apiFetch<{ angebote: OfferTreffer[] }>(`/pricing/offer/search/?q=${encodeURIComponent(q)}`)
  return res.angebote
}

export function getEbayOffer(itemId: string): Promise<EbayOffer> {
  return apiFetch<EbayOffer>(`/pricing/offer/ebay/${encodeURIComponent(itemId)}/`)
}

export function applyOfferStrategy(itemId: string, payload: {
  stock_item_ids: number[]
  strategy: PricingStrategy
  preview_token: string
  request_id: string
}): Promise<BatchResult> {
  return apiFetch<BatchResult>(`/pricing/offer/ebay/${encodeURIComponent(itemId)}/strategy/`, {
    method: 'POST', body: JSON.stringify(payload),
  })
}

export async function listOfferBatches(): Promise<BatchListEintrag[]> {
  const res = await apiFetch<{ batches: BatchListEintrag[] }>('/pricing/offer/strategy-batches/')
  return res.batches
}

export function undoOfferBatch(batchId: string): Promise<UndoResult> {
  return apiFetch<UndoResult>(`/pricing/offer/strategy-batches/${encodeURIComponent(batchId)}/undo/`, { method: 'POST' })
}

/** Server-Gründe (409/404) in Bedienersprache. */
export function offerFehlerText(message: string): string {
  switch (message) {
    case 'stand_veraltet': return 'Die Strategie dieses Angebots hat sich inzwischen geändert. Angebot neu geladen – bitte Vorschau erneut prüfen.'
    case 'nicht_im_angebot': return 'Mindestens ein gewählter Artikel gehört nicht mehr aktiv zu diesem Angebot. Angebot neu geladen – bitte Auswahl prüfen.'
    case 'request_id_anderer_inhalt': return 'Diese Bestätigung wurde schon mit anderer Auswahl gesendet. Bitte Vorschau neu öffnen.'
    case 'bereits_rueckgaengig': return 'Dieser Batch wurde bereits rückgängig gemacht.'
    case 'batch_unbekannt': return 'Batch nicht gefunden.'
    default: return message
  }
}
